// src/pages/executive/widgets/InsightsStripWidget.jsx
//
// Three compact panels for the executive dashboard:
//   1. Data Quality Ring   — /api/admin/analytics/data-quality
//   2. Open Anomalies      — /api/admin/analytics/anomalies
//   3. Leave Liability     — /api/admin/analytics/leave-liability
//
// Each panel has its own loading / error / empty state.

import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, AlertOctagon, Wallet, ArrowRight } from "lucide-react";
import { Skeleton, ErrorState, EmptyState } from "../../../components/ui/LoadingStates";
import ErrorBoundary from "../../../components/ui/ErrorBoundary";

const API = process.env.REACT_APP_API_BASE || "http://127.0.0.1:8000";
const authH = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const NEON  = "#00ff90";
const RED   = "#f87171";
const AMBER = "#fbbf24";
const BLUE  = "#60a5fa";

const cardCls =
  "rounded-2xl border border-[#00ff90]/20 bg-[#080808] shadow-[0_0_24px_#00ff9010] p-5";

export default function InsightsStripWidget({ onAskKira }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <ErrorBoundary variant="panel" label="Data quality"><DataQualityCard onAskKira={onAskKira} /></ErrorBoundary>
      <ErrorBoundary variant="panel" label="Anomalies"><AnomaliesCard   onAskKira={onAskKira} /></ErrorBoundary>
      <ErrorBoundary variant="panel" label="Leave liability"><LeaveLiabilityCard onAskKira={onAskKira} /></ErrorBoundary>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  1. Data Quality Ring
// ─────────────────────────────────────────────────────────
function DataQualityCard({ onAskKira }) {
  const [data, setData]     = useState(null);
  const [loading, setL]     = useState(true);
  const [error,   setE]     = useState(null);

  const load = useCallback(() => {
    setL(true); setE(null);
    fetch(`${API}/api/admin/analytics/data-quality`, { headers: authH() })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setData(d))
      .catch(e => setE(e.message))
      .finally(() => setL(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const score = data?.score ?? null;
  const tint  = score == null ? BLUE : score >= 90 ? NEON : score >= 70 ? AMBER : RED;

  return (
    <div className={cardCls}>
      <Header
        icon={ShieldCheck}
        title="Data Quality"
        sub="HR records health"
        tint={tint}
        onAskKira={() => onAskKira?.(data, "Explain the current data-quality score and what would push it to 100.")}
      />

      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", padding: "24px 0" }}>
          <Skeleton width={120} height={120} radius="50%" />
        </div>
      ) : error ? (
        <ErrorState message="Couldn't load data quality" detail={error} onRetry={load} />
      ) : !data ? (
        <EmptyState title="No data quality info" />
      ) : (
        <div className="flex flex-col items-center" style={{ paddingTop: 8 }}>
          <Ring value={score ?? 0} color={tint} />
          <div className="flex gap-4 mt-3 text-xs">
            <div style={{ color: RED }}>
              <span style={{ fontWeight: 600 }}>{data.critical ?? 0}</span>
              <span style={{ color: "rgba(255,255,255,0.4)", marginLeft: 4 }}>critical</span>
            </div>
            <div style={{ color: AMBER }}>
              <span style={{ fontWeight: 600 }}>{data.warnings ?? 0}</span>
              <span style={{ color: "rgba(255,255,255,0.4)", marginLeft: 4 }}>warnings</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Ring({ value, color }) {
  const radius = 50;
  const stroke = 8;
  const norm   = radius - stroke / 2;
  const circ   = 2 * Math.PI * norm;
  const offset = circ - (Math.max(0, Math.min(100, value)) / 100) * circ;
  return (
    <svg width={120} height={120} viewBox="0 0 120 120">
      <circle cx={60} cy={60} r={norm} stroke="rgba(255,255,255,0.06)"
              strokeWidth={stroke} fill="transparent" />
      <circle cx={60} cy={60} r={norm} stroke={color}
              strokeWidth={stroke} fill="transparent"
              strokeDasharray={`${circ} ${circ}`}
              strokeDashoffset={offset}
              strokeLinecap="round"
              transform="rotate(-90 60 60)"
              style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      <text x="60" y="62" textAnchor="middle" dominantBaseline="middle"
            fill="#fff" fontSize="22" fontWeight="600" fontFamily="monospace">
        {value}
      </text>
      <text x="60" y="80" textAnchor="middle" dominantBaseline="middle"
            fill="rgba(255,255,255,0.4)" fontSize="9" letterSpacing="2">SCORE</text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────
//  2. Open Anomalies
// ─────────────────────────────────────────────────────────
function AnomaliesCard({ onAskKira }) {
  const [data, setData] = useState(null);
  const [loading, setL] = useState(true);
  const [error,   setE] = useState(null);

  const load = useCallback(() => {
    setL(true); setE(null);
    fetch(`${API}/api/admin/analytics/anomalies`, { headers: authH() })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setData(d))
      .catch(e => setE(e.message))
      .finally(() => setL(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const count = data?.anomalies_found ?? 0;
  const tint  = count === 0 ? NEON : count <= 3 ? AMBER : RED;
  const top   = data?.by_type ? Object.entries(data.by_type).slice(0, 3) : [];

  return (
    <div className={cardCls}>
      <Header
        icon={AlertOctagon}
        title="Open Anomalies"
        sub="last scan"
        tint={tint}
        onAskKira={() => onAskKira?.(data, `Explain the ${count} anomalies and rank by business impact.`)}
      />

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8 }}>
          <Skeleton width="40%" height={36} />
          <Skeleton width="80%" height={10} />
          <Skeleton width="60%" height={10} />
        </div>
      ) : error ? (
        <ErrorState message="Couldn't load anomalies" detail={error} onRetry={load} />
      ) : !data ? (
        <EmptyState title="No anomalies data" />
      ) : (
        <div>
          <div style={{
            fontSize: 38, fontWeight: 600, color: tint,
            fontFamily: "monospace", lineHeight: 1, marginTop: 4,
          }}>
            {count}
          </div>
          <p className="text-xs text-white/30 mt-1">
            {count === 0 ? "Nothing flagged — system clean" : "anomalies detected"}
          </p>

          {top.length > 0 && (
            <div style={{
              marginTop: 12, paddingTop: 12,
              borderTop: "0.5px solid rgba(255,255,255,0.06)",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {top.map(([kind, n]) => (
                <div key={kind} style={{
                  display: "flex", justifyContent: "space-between",
                  fontSize: 11, color: "rgba(255,255,255,0.5)",
                }}>
                  <span style={{ textTransform: "capitalize" }}>{kind.replace(/_/g, " ")}</span>
                  <span style={{ color: tint, fontWeight: 600 }}>{n}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  3. Leave Liability
// ─────────────────────────────────────────────────────────
function LeaveLiabilityCard({ onAskKira }) {
  const [data, setData] = useState(null);
  const [loading, setL] = useState(true);
  const [error,   setE] = useState(null);

  const load = useCallback(() => {
    setL(true); setE(null);
    fetch(`${API}/api/admin/analytics/leave-liability`, { headers: authH() })
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => setData(d))
      .catch(e => setE(e.message))
      .finally(() => setL(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data?.total_liability_gbp ?? data?.total_liability ?? data?.total ?? null;
  const days  = data?.total_days_accrued ?? data?.total_days ?? null;
  const byDept = Array.isArray(data?.by_department) ? data.by_department.slice(0, 3) : [];

  const formatted = total != null
    ? `£${Number(total).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
    : "—";

  return (
    <div className={cardCls}>
      <Header
        icon={Wallet}
        title="Leave Liability"
        sub="accrued cost"
        tint={NEON}
        onAskKira={() => onAskKira?.(data, "Explain the leave liability and which teams drive most of it.")}
      />

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingTop: 8 }}>
          <Skeleton width="60%" height={36} />
          <Skeleton width="80%" height={10} />
        </div>
      ) : error ? (
        <ErrorState message="Couldn't load leave liability" detail={error} onRetry={load} />
      ) : !data ? (
        <EmptyState title="No liability data" />
      ) : (
        <div>
          <div style={{
            fontSize: 30, fontWeight: 600, color: NEON,
            fontFamily: "monospace", lineHeight: 1, marginTop: 4,
          }}>
            {formatted}
          </div>
          <p className="text-xs text-white/30 mt-1">
            {days != null ? `${days} days accrued` : "across active staff"}
          </p>

          {byDept.length > 0 && (
            <div style={{
              marginTop: 12, paddingTop: 12,
              borderTop: "0.5px solid rgba(255,255,255,0.06)",
              display: "flex", flexDirection: "column", gap: 6,
            }}>
              {byDept.map((d, i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between",
                  fontSize: 11, color: "rgba(255,255,255,0.5)",
                }}>
                  <span>{d.department || d.name || "—"}</span>
                  <span style={{ color: NEON, fontWeight: 600, fontFamily: "monospace" }}>
                    £{Number(d.liability_gbp || d.liability || d.amount || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
//  Shared header
// ─────────────────────────────────────────────────────────
function Header({ icon: Icon, title, sub, tint, onAskKira }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <div className="flex items-center gap-2">
        <div style={{
          width: 28, height: 28, borderRadius: 8,
          background: `${tint}14`, border: `0.5px solid ${tint}40`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: tint, flexShrink: 0,
        }}>
          <Icon size={14} />
        </div>
        <div>
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 500 }}>{title}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.35)", textTransform: "uppercase", letterSpacing: 1 }}>
            {sub}
          </div>
        </div>
      </div>
      {onAskKira && (
        <button onClick={onAskKira}
          className="text-xs flex items-center gap-1 text-white/30 hover:text-[#00ff90] transition-colors">
          Ask KIRA <ArrowRight size={11} />
        </button>
      )}
    </div>
  );
}
